import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { ErrorHandler, inject } from '@angular/core';
import { Router } from '@angular/router';
import { injectIsControlHub } from '@seatix/shared/core';
import { injectLoadingService } from '@seatix/shared-util-loading';
import { catchError, Observable, switchMap, tap, throwError } from 'rxjs';
import { SpuriousUnauthorizedError } from '../auth-api/session-errors';
import { SessionProbeService } from '../session-probe/session-probe.service';
import { AuthStore } from '../store/auth-store.service';
import { getAuthRetry, isExpectedUnauthorized } from './auth-context';
import { AUTH_DIALOG_HANDLER } from './auth-dialog-handler.token';

/**
 * A report's message becomes a Sentry issue title, and a query string can hold
 * personal data (a search term, an email), so reports name the path only. A
 * caller may also write the query into the URL itself, hence the split.
 */
function pathOf(request: HttpRequest<unknown>): string {
  return request.url.split('?')[0];
}

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const authDialogHandler = inject(AUTH_DIALOG_HANDLER, { optional: true });
  const loadingService = injectLoadingService();
  const router = inject(Router);
  const isControlHub = injectIsControlHub();
  const sessionProbe = inject(SessionProbeService);
  const errorHandler = inject(ErrorHandler);

  /**
   * A 401 on a hub request is not proof that the session ended: `/me` is asked
   * first. A live session keeps the user on the page and gets the request one
   * more try; only a session that `/me` also refuses ends in a logout.
   */
  function confirmSessionExpired(
    request: HttpRequest<unknown>,
    handler: HttpHandlerFn,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    const trigger = {
      method: request.method,
      url: pathOf(request),
      status: error.status,
      responseBody: error.error,
    };

    return sessionProbe.probe(trigger).pipe(
      switchMap(({ state, meStatus }) => {
        if (state !== 'alive') {
          authStore.logoutLocal();
          return throwError(() => error);
        }

        return handler(request.clone()).pipe(
          catchError((retryError: unknown) => {
            if (statusOf(retryError) === HttpStatusCode.Unauthorized) {
              errorHandler.handleError(
                new SpuriousUnauthorizedError({
                  method: request.method,
                  url: pathOf(request),
                  status: HttpStatusCode.Unauthorized,
                  responseBody: (retryError as HttpErrorResponse).error,
                  meStatus,
                }),
              );
            }
            return throwError(() => retryError);
          }),
        );
      }),
    );
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const isLogoutRequest = req.url.includes('/logout');
      if (
        error.status === HttpStatusCode.Unauthorized &&
        !isLogoutRequest &&
        !isExpectedUnauthorized(req)
      ) {
        const { shouldRetry, retryingMessage, message } = getAuthRetry(req);

        if (shouldRetry && authDialogHandler) {
          authStore.clearAuth();
          loadingService.hideAll();
          return authDialogHandler
            .showAuthDialog({
              allowClose: false,
              initialMode: 'social',
              message:
                message ??
                'Tu sesión expiró, ingresa nuevamente para continuar.',
            })
            .pipe(
              switchMap((authenticated) => {
                if (authenticated && authStore.isAuthenticated()) {
                  const loadingRef = loadingService.show({
                    message: retryingMessage ?? 'Reintentando...',
                    cancelable: false,
                  });

                  const retryReq = req.clone();
                  return next(retryReq).pipe(
                    tap({
                      next: () => {
                        loadingRef.close();
                      },
                      error: () => {
                        loadingRef.close();
                      },
                    }),
                  );
                } else {
                  authStore.logoutLocal();
                  router.navigate(['/']);
                  return throwError(() => error);
                }
              }),
            );
        }

        if (isControlHub) {
          return confirmSessionExpired(req, next, error);
        }

        authStore.logoutLocal();
      }
      return throwError(() => error);
    }),
  );
};
